/* Manic Fits — homepage behaviour: hero film pause, athlete rail arrows, bundle builder. */
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* Hero film: pause / play (also respects reduced motion) */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-mh-pause]');
  if (!btn) return;
  const video = btn.closest('.mh-hero') && btn.closest('.mh-hero').querySelector('video');
  if (!video) return;
  const paused = btn.getAttribute('aria-pressed') === 'true';
  if (paused) video.play().catch(() => {});
  else video.pause();
  btn.setAttribute('aria-pressed', String(!paused));
  btn.setAttribute('aria-label', paused ? 'Pause film' : 'Play film');
});
if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const pauseAll = () =>
    document.querySelectorAll('.mh video').forEach((v) => {
      v.pause();
      const b = v.closest('.mh-hero') && v.closest('.mh-hero').querySelector('[data-mh-pause]');
      if (b) {
        b.setAttribute('aria-pressed', 'true');
        b.setAttribute('aria-label', 'Play film');
      }
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pauseAll);
  else pauseAll();
}

/* Hero film: only plays while on screen, and stays still on data-saver connections */
(() => {
  const videos = document.querySelectorAll('[data-mh-hero-video]');
  if (!videos.length) return;
  const conn = navigator.connection || {};
  const saveData = conn.saveData === true || /(^|-)2g$/.test(conn.effectiveType || '');
  const userPaused = (v) => {
    const b = v.closest('.mh-hero') && v.closest('.mh-hero').querySelector('[data-mh-pause]');
    return b && b.getAttribute('aria-pressed') === 'true';
  };
  videos.forEach((v) => {
    if (saveData) {
      v.removeAttribute('autoplay');
      v.preload = 'none';
      v.pause();
      const b = v.closest('.mh-hero') && v.closest('.mh-hero').querySelector('[data-mh-pause]');
      if (b) {
        b.setAttribute('aria-pressed', 'true');
        b.setAttribute('aria-label', 'Play film');
      }
      return;
    }
    /* If the phone blocks autoplay (for example iPhone Low Power Mode), start the film on the first tap. */
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => {
          const go = () => {
            const r = v.getBoundingClientRect();
            if (!userPaused(v) && r.bottom > 0 && r.top < innerHeight) v.play().catch(() => {});
          };
          ['touchend', 'click', 'keydown'].forEach((t) =>
            document.addEventListener(t, go, { once: true, passive: true })
          );
        });
      }
    };
    if (!('IntersectionObserver' in window)) {
      tryPlay();
      return;
    }
    new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            if (!userPaused(v) && v.paused) tryPlay();
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { threshold: 0.05 }
    ).observe(v);
  });
})();

/* Athlete rail arrows */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-mh-rail]');
  if (!btn) return;
  const track = btn.closest('section').querySelector('[data-mh-rail-track]');
  if (!track) return;
  const card = track.firstElementChild;
  const step = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
  track.scrollBy({ left: Number(btn.dataset.mhRail) * step * 2, behavior: 'smooth' });
});

/* Bundle builder: one size per piece -> the matching bundle variant */
class ManicBundle extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    try {
      this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    } catch (e) {
      return;
    }
    this.add = this.querySelector('[data-bundle-add]');
    this.notify = this.querySelector('[data-bundle-notify]');
    this.msg = this.querySelector('[data-bundle-msg]');
    this.addEventListener('change', () => this.sync());
    this.add.addEventListener('click', () => this.addToBag());
    this.sync();
  }
  current() {
    const picks = Array.from(this.querySelectorAll('select[data-option]')).map((s) => s.value);
    return this.data.variants.find((v) => v.options.every((o, i) => o === picks[i])) || null;
  }
  sync() {
    const v = this.current();
    const ok = !!(v && v.available);
    this.add.hidden = !ok;
    this.notify.hidden = ok;
    if (v) this.notify.href = `${this.dataset.url}?variant=${v.id}`;
    const anyAvailable = this.data.variants.some((x) => x.available);
    this.msg.textContent = ok
      ? ''
      : anyAvailable
        ? 'This size combination is restocking. Tap above to get an email when it is back.'
        : 'The full kit is restocking. Pick your sizes and tap above to get an email when it is back.';
  }
  async addToBag() {
    const v = this.current();
    if (!v || !v.available) return this.sync();
    this.add.disabled = true;
    this.msg.textContent = 'Adding…';
    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add the bundle.');
      }
      window.location.href = `${root()}cart`;
    } catch (err) {
      this.msg.textContent = err.message;
      this.add.disabled = false;
    }
  }
}
if (!customElements.get('manic-bundle')) customElements.define('manic-bundle', ManicBundle);

/* Lookbook: Men / Her filter, show more, shop-the-look dialog */
document.addEventListener('click', (e) => {
  const f = e.target.closest('[data-look-filter]');
  if (f) {
    const sec = f.closest('.mh-look');
    const want = f.dataset.lookFilter;
    sec.querySelectorAll('[data-look-filter]').forEach((b) => b.setAttribute('aria-pressed', String(b === f)));
    if (want !== 'all') {
      sec.querySelectorAll('[data-more]').forEach((it) => (it.hidden = false));
      const btn = sec.querySelector('[data-look-more]');
      if (btn) btn.remove();
    }
    sec.querySelectorAll('.mh-look__item').forEach((it) => {
      it.style.display = want === 'all' || it.dataset.aud === want ? '' : 'none';
    });
    return;
  }
  const more = e.target.closest('[data-look-more]');
  if (more) {
    more.closest('.mh-look').querySelectorAll('[data-more]').forEach((it) => (it.hidden = false));
    more.remove();
    return;
  }
  const open = e.target.closest('[data-look-open]');
  if (open) {
    const dlg = open.closest('.mh-look').querySelector('[data-look-dialog]');
    if (!dlg || typeof dlg.showModal !== 'function') {
      window.location.href = open.dataset.url;
      return;
    }
    const d = open.dataset;
    const img = dlg.querySelector('[data-look-img]');
    img.src = d.src;
    img.alt = d.alt || '';
    dlg.querySelector('[data-look-title]').textContent = d.title;
    dlg.querySelector('[data-look-price]').textContent = d.price;
    dlg.querySelector('[data-look-compare]').textContent = d.compare || '';
    const link = dlg.querySelector('[data-look-link]');
    link.href = d.url;
    const soldOut = d.available === 'false';
    link.textContent = soldOut ? 'Notify me when it is back' : 'Shop this piece';
    dlg.querySelector('[data-look-note]').textContent = soldOut
      ? 'This piece is restocking. Leave your email on the product page and we will tell you the moment it is back.'
      : 'Cash on delivery all over Bangladesh. Free shipping above Tk 2,000. 5-day exchange.';
    dlg.showModal();
    return;
  }
  if (e.target.closest('[data-look-close]') || e.target.matches('[data-look-dialog]')) {
    const dlg = e.target.closest('[data-look-dialog]');
    if (dlg) dlg.close();
  }
});

/* Categories: hovering a row swaps the big photo */
function catxActivate(row) {
  const sec = row.closest('[data-catx]');
  if (!sec) return;
  const i = row.dataset.catxRow;
  sec.querySelectorAll('[data-catx-row]').forEach((r) => r.classList.toggle('is-active', r === row));
  const imgs = sec.querySelectorAll('[data-catx-img]');
  const target = sec.querySelector(`[data-catx-img="${i}"]`);
  if (target) imgs.forEach((im) => im.classList.toggle('is-active', im === target));
  const num = sec.querySelector('[data-catx-num]');
  if (num) num.textContent = String(Number(i) + 1).padStart(2, '0');
}
document.addEventListener('mouseover', (e) => {
  const row = e.target.closest && e.target.closest('[data-catx-row]');
  if (row && !row.classList.contains('is-active')) catxActivate(row);
});
document.addEventListener('focusin', (e) => {
  const row = e.target.closest && e.target.closest('[data-catx-row]');
  if (row) catxActivate(row);
});
